import type { ChartSettingsType } from "@/features/Charts/types/ChartType";

/**
 * The value-axis overrides a chart hands ECharts for `min`, `max` and `splitNumber`.
 *
 * The point of this helper is that **every key is always present**. `setOption` merges by default,
 * so a key the option omits keeps whatever was merged in on an earlier draw — omitting is "leave it
 * as it was", never "go back to auto". Spreading the keys in conditionally therefore pins the axis
 * permanently the first time a caller sets a bound: the hotspot deep-dive's Relative view pinned the
 * axis to -100..100, and switching back to Absolute redrew real emission values (~0.01) on that same
 * -100..100 axis, i.e. bars a fraction of a pixel wide. The chart looked like it had lost its bars —
 * axis, category names and value labels all still there — and stayed that way for the rest of the
 * modal session (RTLCA-5705).
 *
 * `null` is ECharts' own "compute this from the data" value, so passing it explicitly is what
 * releases a previously pinned axis. Callers keep the same opt-in API: leave `axisMin`/`axisMax`/
 * `axisSplitNumber` unset and the axis follows the data.
 */
export type ValueAxisOverrides = {
  min: number | null;
  max: number | null;
  splitNumber: number | null;
};

export function getValueAxisOverrides(
  settings: Pick<ChartSettingsType, "axisMin" | "axisMax" | "axisSplitNumber">
): ValueAxisOverrides {
  return {
    // A tick-count hint only when the caller asks for one: forcing it on a chart that does not need
    // it can push the axis maximum past the data's own round number (a 0-100% chart running to 120).
    splitNumber: settings.axisSplitNumber ?? null,
    // Only a caller that knows its own bounds should pin the axis — a chart plotting real quantities
    // wants the axis to follow them.
    min: settings.axisMin ?? null,
    max: settings.axisMax ?? null,
  };
}
